import type { LocalProjectionDomain, RuntimeCursor } from "../gateway/local-projection-client";
import { indexedScope, readLocalProjectionCursor } from "./indexed-runtime";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

// This version belongs to the rebuildable local projection shape, not to Business Master data.
// Bump a domain only when an existing persisted payload can no longer satisfy the current reader.
// A mismatch forces one FULL bootstrap for that domain while leaving Journal/Outbox untouched.
const LOCAL_PROJECTION_SCHEMA_VERSION: Partial<Record<LocalProjectionDomain, number>> = {
  CUSTOMER_CREDIT: 2,
  // v3 adds Organization-owned Stock Group identity/name/sort to the STOCK payload.
  // Existing terminals must receive one FULL rebuild so grouping never depends on stale v2 rows.
  STOCK: 3,
  // Stock history is a rebuildable transaction projection. Versioning belongs to the cache
  // shape only; canonical movement history remains in Stock Core.
  STOCK_HISTORY: 1,
};

function versionKey(identity: TrustedTerminalIdentity, domain: LocalProjectionDomain) {
  return `local-index:schema:${indexedScope(identity, domain.toLocaleLowerCase())}`;
}

export async function readVersionedLocalProjectionCursor(
  identity: TrustedTerminalIdentity,
  domain: LocalProjectionDomain,
): Promise<RuntimeCursor | null> {
  const expectedVersion = LOCAL_PROJECTION_SCHEMA_VERSION[domain];
  if (expectedVersion == null) return readLocalProjectionCursor(identity, domain);

  const row = await posRuntimeDb.runtimeMeta.get(versionKey(identity, domain));
  if (Number(row?.value) !== expectedVersion) return null;
  return readLocalProjectionCursor(identity, domain);
}

export async function markLocalProjectionSchemaCurrent(
  identity: TrustedTerminalIdentity,
  domain: LocalProjectionDomain,
) {
  const expectedVersion = LOCAL_PROJECTION_SCHEMA_VERSION[domain];
  if (expectedVersion == null) return;

  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: versionKey(identity, domain),
    scopeKey: indexedScope(identity, domain.toLocaleLowerCase()),
    value: expectedVersion,
    updatedAt: now,
  });
}
